import { useState } from "react";
import { Check, ArrowRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

export function EventListingForm() {
  const { lang } = useLang();
  const [form, setForm] = useState({ organizer: "", email: "", event_date: "", format: "in-person", title: "", location: "", description: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErr("");
    try {
      await api.post("/submissions", { ...form, lang });
      setStatus("success");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Something went wrong.");
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-3xl bg-white/10 border border-white/15 p-10 text-center" data-testid="event-form-success">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#CAE295] text-slate-950">
          <Check size={32} strokeWidth={3} />
        </span>
        <h3 className="mt-5 font-display font-black italic uppercase text-2xl">You're on the map.</h3>
        <p className="mt-2 text-white/70">Thanks, {form.organizer || "friend"}. Your listing is in review and will be live within days.</p>
        <button
          className="mt-6 rounded-full bg-white text-slate-950 hover:bg-[#CAE295] font-bold px-6 py-3 cursor-pointer transition-colors"
          onClick={() => { setForm({ organizer: "", email: "", event_date: "", format: "in-person", title: "", location: "", description: "" }); setStatus("idle"); }}
          data-testid="event-form-reset"
        >
          List another event
        </button>
      </div>
    );
  }

  const inputCls = "w-full rounded-2xl bg-white/10 border border-white/20 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#2CACD4] focus:ring-2 focus:ring-[#2CACD4]/40 transition";

  return (
    <form onSubmit={submit} className="rounded-3xl bg-white/[0.06] border border-white/15 p-6 sm:p-8" data-testid="event-listing-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="text-sm font-semibold text-white/80 mb-1.5 block">Organizer name</label>
          <input required value={form.organizer} onChange={set("organizer")} className={inputCls} placeholder="Your name" data-testid="event-form-organizer" />
        </div>
        <div>
          <label className="text-sm font-semibold text-white/80 mb-1.5 block">Email</label>
          <input required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.ca" data-testid="event-form-email" />
        </div>
        <div>
          <label className="text-sm font-semibold text-white/80 mb-1.5 block">Event date</label>
          <input required value={form.event_date} onChange={set("event_date")} className={inputCls} placeholder="e.g. Feb 6, 2026" data-testid="event-form-date" />
        </div>
        <div>
          <label className="text-sm font-semibold text-white/80 mb-1.5 block">Format</label>
          <select value={form.format} onChange={set("format")} className={`${inputCls} cursor-pointer`} data-testid="event-form-format">
            <option value="in-person" className="text-slate-900">In-person</option>
            <option value="virtual" className="text-slate-900">Virtual</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="text-sm font-semibold text-white/80 mb-1.5 block">Description</label>
          <textarea rows={3} value={form.description} onChange={set("description")} className={inputCls} placeholder="Tell us about your move…" data-testid="event-form-description" />
        </div>
      </div>

      {status === "error" && <p className="mt-4 text-[#CAE295] text-sm" data-testid="event-form-error">{err}</p>}

      <div className="mt-6 flex flex-col sm:flex-row items-center gap-4">
        <button
          type="submit"
          disabled={status === "loading"}
          className="inline-flex items-center gap-2 rounded-full bg-white text-slate-950 hover:bg-[#CAE295] font-bold px-7 py-3.5 shadow-lg cursor-pointer transition-colors disabled:opacity-60"
          data-testid="event-form-submit"
        >
          {status === "loading" ? "Submitting…" : "Submit free listing"}
          <ArrowRight size={18} strokeWidth={2.5} />
        </button>
        <p className="text-sm text-white/60">Free national listing — live within days.</p>
      </div>
    </form>
  );
}
